import { useCallback, useEffect, useRef, useState } from 'react';

import type { Widget } from '@yandex-int/messenger.widget';

import { destroyChatWidget, initChatWidget, setChatVisibility } from '../../helpers';
import { useWidgetEventListeners, UseWidgetListenersParams } from './widget-event-listeners';

type UseChatWidgetParams = {
    chatId: string | null;
    mountNode: HTMLElement | null;
} & Omit<UseWidgetListenersParams, 'widget'>;

const onDocumentFocusin = () => {
    setChatVisibility(false);
};
const onDocumentFocusout = () => {
    // надо брать activeElement асинхронно, чтобы нода там успело обновиться
    setTimeout(() => {
        if (
            document.activeElement?.tagName === 'IFRAME' &&
            'messengerIframe' in (document.activeElement as HTMLElement)?.dataset
        ) {
            setChatVisibility(true);
        }
    }, 0);
};

export const useChatWidget = ({
    chatId,
    mountNode,
    counter,
    userLoaded,
    ready,
    unload,
    error,
}: UseChatWidgetParams): { widget: Widget | null; isWidgetLoading: boolean } => {
    const prevChatId = useRef<string | null>(null);
    const [isWidgetLoading, setWidgetLoading] = useState(true);
    const [widget, setWidget] = useState<Widget | null>(null);

    const onChatLoadError = useCallback(() => {
        setWidgetLoading(false);
    }, []);

    useEffect(() => {
        /**
         * Создаём экземпляр виджета
         * Изначально в конфе чата не существует,
         * пока его не создаст кто-нибудь из участников (по кнопке открытия чата)
         * Ждём появления chatId в конференции (прилетает из RoomEvents.CLIENT_CHAT_CREATED)
         */
        if (chatId && prevChatId.current !== chatId && mountNode) {
            prevChatId.current = chatId;
            initChatWidget({
                mountNode,
                chatId,
            })
                .then((widgetInstance) => {
                    if (widgetInstance) {
                        setWidget(widgetInstance);
                    }
                })
                .finally(() => {
                    setWidgetLoading(false);
                });
        }
    }, [chatId, mountNode]);

    useEffect(() => {
        if (widget) {
            widget.events.chatLoadError.addListener(onChatLoadError);
        }
        return () => {
            if (widget) {
                destroyChatWidget();
                widget.events.chatLoadError.removeListener(onChatLoadError);
            }
        };
    }, [onChatLoadError, widget]);

    useWidgetEventListeners({
        widget,
        counter,
        userLoaded,
        ready,
        unload,
        error,
    });

    useEffect(() => {
        /*
            следим за фокусом чата и управляем авто доскроллом сами
            потому что виджет по ошибке считает, что у него есть фокус
            до того как первый раз его поулчит
            наш тикет - https://st.yandex-team.ru/TELEMOSTFRONT-1423
            тикет на фикс бага в виджете - https://st.yandex-team.ru/MSSNGRFRONT-14128
            todo: когда баг в виджете пофиксят надо будет ревертнуть коммит
        */
        document.addEventListener('focusin', onDocumentFocusin);
        document.addEventListener('focusout', onDocumentFocusout);
        return () => {
            destroyChatWidget();
            document.removeEventListener('focusin', onDocumentFocusin);
            document.removeEventListener('focusout', onDocumentFocusout);
        };
    }, []);

    return { widget, isWidgetLoading };
};

export default useChatWidget;
